


































































































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import { api } from '@/api';
import { Role, User, UserAgreement } from '@/interfaces/Models';
import { Debounce } from 'lodash-decorators';
import moment from 'moment';

@Component
export default class UserTable extends Vue {
  showEmailsDialog = false;
  showAgreementsDialog = false;
  selectedUser: User | null = null;
  sentEmails: any[] = [];
  userAgreements: UserAgreement[] = [];
  emailsLoading = false;
  agreementsLoading = false;
  roles: Role[] = [];
  rolesLoading = true;
  users: User[] = [];
  usersLoading = false;
  pagination = {
    per_page: 10,
    page: 1,
  };
  totalItems = 0;
  filters = {
    first_name: '',
    last_name: '',
    email: '',
    roles: [] as number[],
    deactivated: false,
  };

  getUserStatusColor(user: User): string {
    if (user.deactivated_at) {
      return 'red';
    }
    if (!user.promoted_at && !user.email_verified_at) {
      return 'orange';
    }
    return 'green';
  }

  get headersLocalized(): any[] {
    return [
      {
        text: this.$t('table_headers.shared.first_name'),
        value: 'first_name',
      },
      {
        text: this.$t('table_headers.shared.last_name'),
        value: 'last_name',
      },
      {
        text: this.$t('table_headers.users.email'),
        value: 'email',
      },
      {
        text: this.$t('table_headers.users.role'),
        value: 'role.name',
      },
      {
        text: this.$t('table_headers.users.deactivated_at'),
        value: 'deactivated_at',
      },
      {
        text: this.$t('table_headers.users.created_at'),
        value: 'created_at',
      },
      {
        text: this.$t('table_headers.shared.actions'),
        value: 'actions',
        align: 'end',
      },
    ];
  }

  get websiteBaseUrl(): string {
    return process.env.VUE_APP_WEBSITE || '';
  }

  getQuestionnaireLink(user: User): string | null {
    if (
      user.role &&
      user.role.id === 5 &&
      !user.promoted_at &&
      !user.email_verified_at
    ) {
      const emailIdentifier =
        user.subscription && user.subscription.email_identifier;
      const parentIdentifier = encodeURIComponent(emailIdentifier);
      const firstName = encodeURIComponent(user.first_name);
      const lastName = encodeURIComponent(user.last_name);
      const email = encodeURIComponent(user.email);
      return `${this.websiteBaseUrl}/questionnaire-parent?parentIdentifier=${parentIdentifier}&cityId=1&locale=en&email=${email}&firstName=${firstName}&lastName=${lastName}`;
    }
    return null;
  }

  public setUsers(data: any) {
    this.users = data.data.filter((user: User) => {
      if (this.filters.deactivated) {
        return user.deactivated_at;
      }
      return !user.deactivated_at;
    });
    this.totalItems = data.meta.total;
  }

  public editUser(user: any) {
    this.$router.push({
      name: 'profile.edit-profile',
      params: { id: user.id, userData: user },
    });
  }

  public updateUserInTable(user: User) {
    const index = this.users.findIndex((item: User) => item.id === user.id);
    if (index !== -1) {
      this.$set(this.users, index, user);
    }
  }

  public reactivateUser(user: User) {
    this.usersLoading = true;
    axios
      .post(api.path('v1.admin.users.reactivate', { id: user.id }))
      .then((res) => this.updateUserInTable(res.data.data))
      .finally(() => {
        this.usersLoading = false;
        this.loadUsers();
      });
  }

  public isQuestionnaireCompleted(user: User): boolean {
    return this.getCircleColor(user) === 'green';
  }

  public promoteUser(user: User) {
    if (
      !this.isQuestionnaireCompleted(user) &&
      user.role &&
      user.role.id !== 6
    ) {
      this.$toast.error('Cannot promote user. Questionnaire not completed.');
      return;
    }

    this.usersLoading = true;
    axios
      .post(api.path('v1.admin.users.promote', { id: user.id }))
      .then((res) => {
        const updatedUser = res.data.data;
        console.log('🚀 ~ UserTable ~ .then ~ updatedUser:', updatedUser);
        this.updateUserInTable(updatedUser);
        this.$toast.success('User promoted successfully');
        // Update the local user object to reflect the new role immediately
        const index = this.users.findIndex((u: User) => u.id === user.id);
        if (index !== -1) {
          this.$set(this.users, index, updatedUser);
        }
      })
      .catch((error) => {
        console.error('Error promoting user:', error);
        this.$toast.error('Failed to promote user');
      })
      .finally(() => {
        this.usersLoading = false;
        this.loadUsers();
      });
  }

  public deactivateUser(user: User) {
    this.usersLoading = true;
    axios
      .post(api.path('v1.admin.users.deactivate', { id: user.id }))
      .then((res) => this.updateUserInTable(res.data.data))
      .finally(() => {
        this.usersLoading = false;
        this.loadUsers();
      });
  }

  public loadUsers() {
    this.usersLoading = true;

    let params: any = {
      ...this.filters,
      page: this.pagination.page,
      per_page: this.pagination.per_page,
    };

    axios
      .get(api.path('v1.admin.users'), { params: params })
      .then((res) => this.setUsers(res.data))
      .catch((error) => console.error('Error loading users:', error))
      .finally(() => (this.usersLoading = false));
  }

  @Watch('pagination.page')
  onPageChange() {
    if (this.$vuetify.breakpoint.mobile) {
      this.$vuetify.goTo(0);
    }
    this.loadUsers();
  }

  @Watch('filters', { deep: true })
  @Debounce(500)
  onFilterValueChange() {
    this.pagination.page = 1;
    this.loadUsers();
  }

  public loadRoles() {
    let params = {
      paginate: 'no',
    };

    axios
      .get(api.path('v1.admin.roles'), { params: params })
      .then((res) => this.setRoles(res.data.data))
      .catch((error) => console.error('Error loading roles:', error))
      .finally(() => (this.rolesLoading = false));
  }

  public setRoles(roles: Role[]): void {
    this.roles = roles;
  }

  public async mounted() {
    await this.loadRoles();
    this.loadUsers();
  }

  public async showSentEmails(user: User) {
    this.selectedUser = user;
    this.showEmailsDialog = true;
    await this.refreshEmailData();
  }

  public async showUserAgreements(user: User) {
    this.selectedUser = user;
    this.showAgreementsDialog = true;
    await this.refreshAgreementsData();
  }

  public async refreshEmailData() {
    if (!this.selectedUser) return;

    this.emailsLoading = true;
    try {
      const response = await axios.get(api.path('v1.admin.sentEmails'), {
        params: { user_id: this.selectedUser.id },
      });
      this.sentEmails = response.data.data.map((email: any) => {
        const emailStatus = email.email_status || {};
        return {
          ...email,
          sent_at: email.sent_at
            ? new Date(email.sent_at).toLocaleString()
            : '-',
          status: emailStatus.last_event || '-',
          delivered_at: emailStatus.delivered_at
            ? new Date(emailStatus.delivered_at).toLocaleString()
            : '-',
          opened_at: emailStatus.opened_at
            ? new Date(emailStatus.opened_at).toLocaleString()
            : '-',
          clicked_at: emailStatus.clicked_at
            ? new Date(emailStatus.clicked_at).toLocaleString()
            : '-',
          failed_at: emailStatus.failed_at
            ? new Date(emailStatus.failed_at).toLocaleString()
            : '-',
          clicked_urls: emailStatus.clicked_urls || [],
          reason: emailStatus.failed_reason || '-',
        };
      });
    } catch (error) {
      console.error('Error fetching sent emails:', error);
      this.sentEmails = []; // Ensure sentEmails is always an array
    } finally {
      this.emailsLoading = false;
    }
  }

  public async refreshAgreementsData() {
    if (!this.selectedUser) return;

    this.agreementsLoading = true;
    try {
      const response = await axios.get(api.path('v1.admin.userAgreements'), {
        params: { user_id: this.selectedUser.id },
      });
      this.userAgreements = response.data.agreements.map(
        (agreement: UserAgreement) => ({
          ...agreement,
          accepted_at: agreement.accepted_at
            ? new Date(agreement.accepted_at).toLocaleString()
            : '-',
          rejected_at: agreement.rejected_at
            ? new Date(agreement.rejected_at).toLocaleString()
            : '-',
          created_at: new Date(agreement.created_at).toLocaleString(),
          updated_at: new Date(agreement.updated_at).toLocaleString(),
        })
      );
    } catch (error) {
      console.error('Error fetching user agreements:', error);
      this.userAgreements = []; // Ensure userAgreements is always an array
    } finally {
      this.agreementsLoading = false;
    }
  }

  resetPasswordLink = '';

  public async getAndCopyResetPasswordLink(user: User) {
    try {
      const response = await axios.post(api.path('v1.admin.password.forgot'), {
        user_id: user.id,
      });
      const resetPasswordLink = response.data.reset_link;

      await navigator.clipboard.writeText(resetPasswordLink);
      this.$toast.success('Reset password link copied to clipboard.');
    } catch (error) {
      console.error('Error generating or copying reset password link:', error);
      this.$toast.error('Failed to generate or copy reset password link.');
    }
  }

  public async sendResetPasswordEmail(user: User) {
    try {
      await axios.post(api.path('v1.admin.password.send'), {
        email: user.email,
        user_id: user.id,
      });
      this.$toast.success('Reset password email sent successfully.');
    } catch (error) {
      console.error('Error sending reset password email:', error);
      this.$toast.error('Failed to send reset password email.');
    }
  }

  public copyQuestionnaireLink(user: User) {
    const link = this.getQuestionnaireLink(user);
    if (link) {
      navigator.clipboard.writeText(link).then(
        () => {
          this.$toast.success('Questionnaire link copied to clipboard.');
        },
        (err) => {
          console.error('Could not copy questionnaire link: ', err);
          this.$toast.error('Failed to copy questionnaire link.');
        }
      );
    }
  }

  public getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'green';
      case 'opened':
        return 'blue';
      case 'failed':
        return 'red';
      case '-':
        return 'grey';
      default:
        return 'orange';
    }
  }

  public getStatusText(status: string): string {
    return status === '-' ? 'Sent' : status;
  }

  public getReadableEmailType(type: string): string {
    const typeMap: { [key: string]: string } = {
      password_set: 'Initial Password Setup',
      new_user_offer: 'New User Questionnaire',
      new_user_follow_up: 'New User Follow-up',
      new_user_feedback: 'New User Feedback',
    };
    return typeMap[type] || type;
  }

  public getHumanReadableAgreementType(type: string): string {
    const typeMap: { [key: string]: string } = {
      PRIVACY_POLICY: 'Privacy Policy',
      BABYSITTER_COMPANY_CONTRACT_PART_TIME: 'Babysitter Contract PT',
      BABYSITTER_COMPANY_CONTRACT_FULL_TIME: 'Babysitter Contract FT',
      PARENT_COMPANY_CONTRACT_PART_TIME: 'Parent Contract PT',
      PARENT_COMPANY_CONTRACT_FULL_TIME: 'Parent Contract FT',
      BABYSITTER_PARENT_CONTRACT_PART_TIME: 'Babysitter and Parent Contract PT',
      BABYSITTER_PARENT_CONTRACT_FULL_TIME: 'Babysitter and Parent Contract FT',
      ANIMATION_CONTRACT: 'Animation Contract',
    };
    return typeMap[type] || type;
  }

  async copyEmail(item: User) {
    try {
      await navigator.clipboard.writeText(item.email);
      this.$toast.success('Email copied to clipboard.');
    } catch (err) {
      console.error('Could not copy email: ', err);
      this.$toast.error('Failed to copy email.');
    }
  }

  public getCircleColor(user: User): string {
    if (!user.promoted_at && !user.email_verified_at) {
      const oneWeekAgo = moment().subtract(7, 'days');
      return moment(user.created_at).isBefore(oneWeekAgo) ? 'red' : 'orange';
    } else {
      return 'green';
    }
  }

  public getUserStatusText(user: User): string {
    const color = this.getCircleColor(user);
    switch (color) {
      case 'green':
        return 'Questionnaire completed, ready for promotion.';
      case 'orange':
        return 'Questionnaire not completed, signed up less than a week ago.';
      case 'red':
        return 'Questionnaire not completed, signed up more than a week ago.';
      default:
        return 'Unknown status';
    }
  }

  public getStatusIcon(user: User): string {
    if (user.promoted_at || user.email_verified_at) return 'mdi-check-circle';
    const oneWeekAgo = moment().subtract(7, 'days');
    return moment(user.created_at).isBefore(oneWeekAgo)
      ? 'mdi-alert-circle'
      : 'mdi-clock-outline';
  }
}
